import { useState } from "react"
import { Alert, Button, Form, Input } from "antd"
import { Lock, Mail } from "lucide-react"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useLocation, useNavigate } from "react-router-dom"
import type { LoginRequest } from "@csbi/contracts"
import { login } from "../../features/tiles/api.js"
import { ApiError } from "../../shared/api/request.js"
import { SITE_NAME } from "../../shared/config/site.js"

const BACKDROP_RATIO = 404 / 1920

export function LoginPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const qc = useQueryClient()
  const [failure, setFailure] = useState<string>()

  const submit = useMutation({
    mutationFn: (values: LoginRequest) => login(values),
    onSuccess: (staff) => {
      qc.setQueryData(["auth", "me"], staff)
      const from = (location.state as { from?: string } | null)?.from
      void navigate(from && from !== "/login" ? from : "/", { replace: true })
    },
    onError: (err) =>
      setFailure(
        err instanceof ApiError ? err.message : "Could not reach the server. Check that the service is running.",
      ),
  })

  return (
    <div
      style={{
        position: "relative",
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 32,
        padding: 24,
        overflow: "hidden",
        background: "var(--csbi-surface)",
      }}
    >
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          insetInline: 0,
          bottom: 0,
          height: `${BACKDROP_RATIO * 100}vw`,
          maxHeight: 404,
          pointerEvents: "none",
          backgroundImage: "url('/image/login-bg.png')",
          backgroundRepeat: "no-repeat",
          backgroundPosition: "bottom center",
          backgroundSize: "100% auto",
          opacity: "var(--csbi-login-backdrop-opacity, 1)",
        }}
      />

      <div style={{ position: "relative", textAlign: "center" }}>
        <img className="csbi-logo" src="/image/logo.png" alt={SITE_NAME} style={{ height: 34, marginBottom: 14 }} />
        <div style={{ fontSize: 15, color: "var(--csbi-ink-secondary)" }}>Client Success BI</div>
        <div style={{ fontSize: 13, color: "var(--csbi-ink-muted)", marginTop: 4 }}>
          Cross-deployment metrics for RegDesk customer deployments
        </div>
      </div>

      <div style={{ position: "relative", width: 300 }}>
        {failure && (
          <Alert
            type="error"
            showIcon
            message={failure}
            style={{ marginBottom: 20 }}
            closable
            onClose={() => setFailure(undefined)}
          />
        )}

        <Form
          layout="vertical"
          requiredMark={false}
          onFinish={(v: LoginRequest) => {
            setFailure(undefined)
            submit.mutate(v)
          }}
        >
          <Form.Item name="account" rules={[{ required: true, message: "Enter your account" }]}>
            <Input
              autoFocus
              size="large"
              autoComplete="username"
              placeholder="Account"
              prefix={<Mail size={15} style={{ color: "var(--csbi-ink-muted)" }} />}
            />
          </Form.Item>

          <Form.Item name="password" rules={[{ required: true, message: "Enter your password" }]}>
            <Input.Password
              size="large"
              autoComplete="current-password"
              placeholder="Password"
              prefix={<Lock size={15} style={{ color: "var(--csbi-ink-muted)" }} />}
            />
          </Form.Item>

          <Button type="primary" size="large" htmlType="submit" block loading={submit.isPending}>
            Sign in
          </Button>
        </Form>
      </div>
    </div>
  )
}
